import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Input from '@mui/material/Input';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import { withStyles } from '@mui/styles';
import { hexToRgb } from 'assets/jss/material-dashboard-pro-react';

const containerFluid = {
  paddingRight: '15px',
  paddingLeft: '15px',
  marginRight: 'auto',
  marginLeft: 'auto',
  '&:before,&:after': {
    display: 'table',
    content: '" "',
  },
  '&:after': {
    clear: 'both',
  },
};

const container = {
  paddingRight: '15px',
  paddingLeft: '15px',
  marginRight: 'auto',
  marginLeft: 'auto',
  '@media (min-width: 768px)': {
    width: '750px',
  },
  '@media (min-width: 992px)': {
    width: '970px',
  },
  '@media (min-width: 1200px)': {
    width: '1170px',
  },
  '&:before,&:after': {
    display: 'table',
    content: '" "',
  },
  '&:after': {
    clear: 'both',
  },
};

const defaultFont = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: '300',
  lineHeight: '1.5em',
};

const primaryColor = [
  '#00A186',
  '#ab47bc',
  '#8e24aa',
  '#af2cc5',
  '#7b1fa2',
  '#7b1fa0',
  '#7b1fa2',
];
const secondaryColor = [
  '#000',
  '#777',
  '#3C4858',
  '#999',
  '#F2F2F2',
  '#DDD',
  '#000',
];
const warningColor = [
  '#000',
  '#ffa726',
  '#fb8c00',
  '#ffa21a',
  '#f57c00',
  '#faf2cc',
  '#fcf8e3',
];
const dangerColor = [
  '#f44336',
  '#ef5350',
  '#e53935',
  '#f55a4e',
  '#d32f2f',
  '#ebcccc',
  '#f2dede',
];
const successColor = [
  '#4caf50',
  '#66bb6a',
  '#43a047',
  '#5cb860',
  '#388e3c',
  '#d0e9c6',
  '#dff0d8',
];
const infoColor = [
  '#00A186',
  '#E77B2B',
  '#379BF7',
  '#F48A3E',
  '#0097a7',
  '#c4e3f3',
  '#d9edf7',
];
const blackColor = '#000';
const whiteColor = '#FFF';

const title = {
  color: secondaryColor[2],
  textDecoration: 'none',
  fontWeight: '300',
  marginTop: '30px',
  marginBottom: '25px',
  minHeight: '32px',
  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  '& small': {
    color: secondaryColor[1],
    fontSize: '65%',
    fontWeight: '400',
    lineHeight: '1',
  },
};

// Color for sidebar
const sideBarColor = '#000';

const boxShadow = {
  boxShadow:
    '0 10px 30px -12px rgba(' +
    hexToRgb(blackColor) +
    ', 0.42), 0 4px 25px 0px rgba(' +
    hexToRgb(blackColor) +
    ', 0.12), 0 8px 10px -5px rgba(' +
    hexToRgb(blackColor) +
    ', 0.2)',
};

const defaultBoxShadow = {
  border: '0',
  borderRadius: '3px',
  boxShadow:
    '0 10px 20px -12px rgba(' +
    hexToRgb(blackColor) +
    ', 0.42), 0 3px 20px 0px rgba(' +
    hexToRgb(blackColor) +
    ', 0.12), 0 8px 10px -5px rgba(' +
    hexToRgb(blackColor) +
    ', 0.2)',
  padding: '10px 0',
  transition: 'all 150ms ease 0s',
};

const primaryBoxShadow = {
  boxShadow:
    '0 4px 20px 0 rgba(' +
    hexToRgb(blackColor) +
    ',.14), 0 7px 10px -5px rgba(' +
    hexToRgb(primaryColor[0]) +
    ',.4)',
};
const infoBoxShadow = {
  boxShadow:
    '0 4px 20px 0 rgba(' +
    hexToRgb(blackColor) +
    ',.14), 0 7px 10px -5px rgba(' +
    hexToRgb(infoColor[0]) +
    ',.4)',
};
const successBoxShadow = {
  boxShadow:
    '0 4px 20px 0 rgba(' +
    hexToRgb(blackColor) +
    ',.14), 0 7px 10px -5px rgba(' +
    hexToRgb(successColor[0]) +
    ',.4)',
};
const warningBoxShadow = {
  boxShadow:
    '0 4px 20px 0 rgba(' +
    hexToRgb(blackColor) +
    ',.14), 0 7px 10px -5px rgba(' +
    hexToRgb(warningColor[0]) +
    ',.4)',
};
const dangerBoxShadow = {
  boxShadow:
    '0 4px 20px 0 rgba(' +
    hexToRgb(blackColor) +
    ',.14), 0 7px 10px -5px rgba(' +
    hexToRgb(dangerColor[0]) +
    ',.4)',
};
const roseBoxShadow = {
  boxShadow:
    '0 4px 20px 0 rgba(' +
    hexToRgb(blackColor) +
    ',.14), 0 7px 10px -5px rgba(' +
    hexToRgb(primaryColor[0]) +
    ',.4)',
};

const ThemedCheckbox = withStyles({
  root: {
    color: primaryColor[0],
    '&$checked': {
      color: primaryColor[0],
    },
  },
  checked: {},
})((props) => <Checkbox color='primary' {...props} />);

const ThemedRadio = withStyles({
  root: {
    color: primaryColor[0],
    '&$checked': {
      color: primaryColor[0],
    },
  },
  checked: {},
})((props) => <Radio color='primary' {...props} />);

const ThemedInput = withStyles({
  root: {
    '&:after': {
      borderBottom: `2px solid ${primaryColor[0]}`,
    },
  },
})((props) => <Input color='primary' {...props} />);

const ThemedText = withStyles({
  root: {
    borderBottom: `2px solid ${primaryColor[0]} !important`,
    '&:after': {
      borderBottom: `2px solid ${primaryColor[0]} !important`,
    },
  },
})((props) => <TextField variant="standard" color='primary' {...props} ç />);

const ThemedSecondaryBtn = withStyles({
  root: {
    color: secondaryColor[3],
    backgroundColor: secondaryColor[4],
    '&:hover': {
      color: secondaryColor[3],
      backgroundColor: secondaryColor[4],
    },
    '& i.fas': {
      padding: `3px`,
    },
  },
})((props) => <Button color='secondary' {...props} />);

const ThemedPrimaryBtn = withStyles({
  root: {
    color: whiteColor,
    backgroundColor: primaryColor[0],
    '&:hover': {
      backgroundColor: primaryColor[0],
    },
    '&:disabled': {
      backgroundColor: secondaryColor[4],
    },
  },
})((props) => <Button color='primary' {...props} />);

export {
  blackColor,
  boxShadow,
  container,
  containerFluid,
  dangerBoxShadow,
  dangerColor,
  defaultBoxShadow,
  defaultFont,
  infoBoxShadow,
  infoColor,
  primaryBoxShadow,
  primaryColor,
  roseBoxShadow,
  secondaryColor,
  sideBarColor,
  successBoxShadow,
  successColor,
  ThemedCheckbox,
  ThemedInput,
  ThemedPrimaryBtn,
  ThemedRadio,
  ThemedSecondaryBtn,
  ThemedText,
  title,
  warningBoxShadow,
  warningColor,
  whiteColor,
};
